/**
 * Payment Gateway Recon — Stage 1 (READ-ONLY worksheet)
 *
 * Upload provider report files (Razorpay: payments + settlements OR
 * Pine Labs: All-Txns + MPR). Backend auto-detects file kind, parses,
 * runs cross-checks, and returns a worksheet.
 *
 * Nothing posts to any journal / ledger / customer record in this stage.
 * Once you've eyeballed the numbers against the provider's own dashboard
 * for a whole month, we move to Stage 2 (Leg A postings).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { FileArrowUp, Warning, CheckCircle, XCircle, Info, TrashSimple } from "@phosphor-icons/react";

export default function PgRecon() {
    const [uploads, setUploads] = useState([]);
    const [selected, setSelected] = useState(null);
    const [worksheet, setWorksheet] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [tab, setTab] = useState("");
    const [openPayouts, setOpenPayouts] = useState([]);
    const [mappingQueue, setMappingQueue] = useState([]);
    const [customerLedgers, setCustomerLedgers] = useState([]);
    const [gstInvoices, setGstInvoices] = useState([]);
    const [openAdjustments, setOpenAdjustments] = useState([]);

    const loadUploads = async () => {
        try {
            const { data } = await api.get("/pg-recon/uploads");
            setUploads(data);
            if (data.length && !selected) setSelected(data[0].id);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const loadOpenPayouts = async () => {
        try {
            const { data } = await api.get("/pg-recon/payouts/open");
            setOpenPayouts(data);
        } catch (e) { /* silent */ }
    };
    const loadMappingQueue = async (uploadId) => {
        try {
            const { data } = await api.get("/pg-recon/customer-mapping-queue", {
                params: uploadId ? { upload_id: uploadId } : {},
            });
            setMappingQueue(data);
        } catch (e) { /* silent */ }
    };
    const loadCustomerLedgers = async () => {
        try {
            const { data } = await api.get("/ledgers");
            setCustomerLedgers((data || []).filter((l) => l.party_type === "customer"));
        } catch (e) { /* silent */ }
    };
    const loadGstInvoices = async () => {
        try {
            const { data } = await api.get("/pg-recon/invoices");
            setGstInvoices(data);
        } catch (e) { /* silent */ }
    };
    const loadOpenAdjustments = async () => {
        try {
            const { data } = await api.get("/pg-recon/adjustments/open");
            setOpenAdjustments(data);
        } catch (e) { /* silent */ }
    };
    useEffect(() => {
        loadUploads(); loadOpenPayouts(); loadCustomerLedgers();
        loadGstInvoices(); loadOpenAdjustments();
    }, []);

    useEffect(() => {
        if (!selected) { setWorksheet(null); return; }
        (async () => {
            try {
                const { data } = await api.get(`/pg-recon/uploads/${selected}`);
                setWorksheet(data);
                if (data.upload.provider === "razorpay") setTab("payments");
                else setTab("transactions");
                loadMappingQueue(selected);
            } catch (e) { toast.error(formatApiError(e)); setWorksheet(null); }
        })();
    }, [selected]);

    const removeUpload = async (id) => {
        if (!window.confirm("Discard this parsed worksheet? Any posted Leg-A journals must be reversed first.")) return;
        try {
            await api.delete(`/pg-recon/uploads/${id}`);
            toast.success("Upload discarded");
            if (selected === id) setSelected(null);
            loadUploads();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const postLegA = async () => {
        if (!selected) return;
        const posted = worksheet?.sections?.totals?.leg_a_posted_count || 0;
        const pending = worksheet?.sections?.totals?.leg_a_pending_count || 0;
        if (!pending) return toast(`All ${posted} rows already posted. Nothing to do.`);
        if (!window.confirm(`Post Leg A vouchers for ${pending} transaction${pending === 1 ? "" : "s"}?\n\nDr Payment Gateway Charges (MDR+platform)\nDr GST Input - Pending Invoice (GST on fee)\nDr Gateway - In Transit (net)\nCr Collections - Unallocated (Suspense) (gross)\n\nEvery voucher is atomic. Idempotent — already-posted rows are skipped.`)) return;
        try {
            const { data } = await api.post(`/pg-recon/uploads/${selected}/post-leg-a`);
            toast.success(`Posted ${data.posted}, skipped ${data.skipped_already_posted}${data.held_as_exception.length ? `, ${data.held_as_exception.length} held as exception` : ""}`);
            const { data: ws } = await api.get(`/pg-recon/uploads/${selected}`);
            setWorksheet(ws);
            loadOpenPayouts();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const reverseLegA = async () => {
        if (!selected) return;
        const posted = worksheet?.sections?.totals?.leg_a_posted_count || 0;
        if (!posted) return toast("Nothing to reverse.");
        if (!window.confirm(`Reverse ${posted} Leg-A voucher${posted === 1 ? "" : "s"}?\n\nEach will get a counter-journal (auditable). The rows' leg_a_journal_id flags are cleared so they can be re-posted from a fresh parse.`)) return;
        try {
            const { data } = await api.post(`/pg-recon/uploads/${selected}/reverse-leg-a`);
            toast.success(`Reversed ${data.reversed} vouchers`);
            const { data: ws } = await api.get(`/pg-recon/uploads/${selected}`);
            setWorksheet(ws);
            loadOpenPayouts();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const bridgePayout = async (payoutKey, bankLineId) => {
        if (!payoutKey || !bankLineId) return;
        try {
            const { data } = await api.post(
                `/pg-recon/payouts/${encodeURIComponent(payoutKey)}/bridge`,
                null,
                { params: { bank_line_id: bankLineId } }
            );
            toast.success(`Bridged. Journal ${data.journal_id.slice(0, 8)}…${data.drift_absorbed ? ` (₹${data.drift_absorbed} drift absorbed)` : ""}`);
            loadOpenPayouts();
            if (selected) {
                const { data: ws } = await api.get(`/pg-recon/uploads/${selected}`);
                setWorksheet(ws);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const unbridgePayout = async (payoutKey) => {
        if (!window.confirm(`Unbridge payout ${payoutKey}?\n\nThe Dr Bank / Cr In-Transit journal is reversed and the bank line is released back to 'open'.`)) return;
        try {
            await api.post(`/pg-recon/payouts/${encodeURIComponent(payoutKey)}/unbridge`);
            toast.success("Unbridged");
            loadOpenPayouts();
            if (selected) {
                const { data: ws } = await api.get(`/pg-recon/uploads/${selected}`);
                setWorksheet(ws);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const mapCustomer = async (bucket, choice) => {
        // choice = { customer_ledger_id } OR { new_customer_parts, gstin }
        try {
            const payload = {
                provider: bucket.provider,
                key_type: bucket.key_type,
                key_norm: bucket.key_norm,
                post_leg_b_now: true,
                ...choice,
            };
            const { data } = await api.post("/pg-recon/customer-mapping", payload);
            toast.success(`Mapped → ${data.customer_name}. Leg B posted for ${data.leg_b_posted} txn${data.leg_b_posted === 1 ? "" : "s"}.`);
            loadMappingQueue(selected);
            loadCustomerLedgers();
            if (selected) {
                const { data: ws } = await api.get(`/pg-recon/uploads/${selected}`);
                setWorksheet(ws);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const reverseLegB = async () => {
        if (!selected) return;
        const postedB = worksheet?.sections?.totals?.leg_b_posted_count || 0;
        if (!postedB) return toast("No Leg B to reverse.");
        if (!window.confirm(`Reverse ${postedB} Leg-B voucher${postedB === 1 ? "" : "s"}?\n\nDr customer / Cr Suspense reverses the customer attribution. Any indicative advances matched during Leg B are released back to 'expected'.`)) return;
        try {
            const { data } = await api.post(`/pg-recon/uploads/${selected}/reverse-leg-b`);
            toast.success(`Reversed ${data.reversed} Leg-B vouchers`);
            loadMappingQueue(selected);
            const { data: ws } = await api.get(`/pg-recon/uploads/${selected}`);
            setWorksheet(ws);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const uploadGstInvoice = async (provider, month, file) => {
        try {
            const fd = new FormData();
            fd.append("provider", provider);
            fd.append("month", month);
            fd.append("file", file);
            const { data } = await api.post("/pg-recon/invoices/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const m = data.three_way_match;
            toast.success(m.matched
                ? `Invoice ingested. 3-way MATCH ✓ — expected ₹${m.expected_gst_from_reports} vs invoice ₹${m.invoice_gst}.`
                : `Invoice ingested. 3-way MISMATCH — drift ₹${m.drift}. Fix before releasing ITC.`);
            loadGstInvoices();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const releaseItc = async (invoiceId, override) => {
        try {
            const { data } = await api.post(`/pg-recon/invoices/${invoiceId}/release-itc`, override || {});
            toast.success(`ITC released. Journal ${data.journal_id.slice(0, 8)}…`);
            loadGstInvoices();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const reverseItc = async (invoiceId) => {
        if (!window.confirm("Reverse ITC release?\n\nA counter-journal is posted. The pending GST goes back to holding.")) return;
        try {
            await api.post(`/pg-recon/invoices/${invoiceId}/reverse-itc`);
            toast.success("ITC reversed");
            loadGstInvoices();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const deleteInvoice = async (invoiceId) => {
        if (!window.confirm("Delete this GST invoice entry? (Refuses if ITC was already released.)")) return;
        try {
            await api.delete(`/pg-recon/invoices/${invoiceId}`);
            toast.success("Invoice deleted");
            loadGstInvoices();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const postAdjustment = async (naturalKey) => {
        if (!window.confirm(`Post this adjustment/recovery to the Gateway Adjustments ledger?\n\nDr Gateway Adjustments / Recovery\nCr Gateway - In Transit\n\nManual review required — no customer ledger is reversed.`)) return;
        try {
            const { data } = await api.post(`/pg-recon/adjustments/${encodeURIComponent(naturalKey)}/post`);
            toast.success(`Adjustment ₹${data.amount} posted`);
            loadOpenAdjustments();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const reverseAdjustment = async (naturalKey) => {
        if (!window.confirm("Reverse this adjustment posting?")) return;
        try {
            await api.post(`/pg-recon/adjustments/${encodeURIComponent(naturalKey)}/reverse`);
            toast.success("Adjustment reversed");
            loadOpenAdjustments();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Payment Gateway Reconciliation"
                sub={<>Stage 4 &nbsp;<span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300">All stages live — Leg A · Bank bridge · Leg B · ITC · Adjustments</span> — Fees + GST read verbatim; MDR/GST never recomputed.</>}
            />
            <div className="p-8 space-y-6">
                <UploadCard onUploaded={(id) => { setSelected(id); loadUploads(); }}
                             uploading={uploading} setUploading={setUploading} />

                {uploads.length ? (
                    <UploadPicker uploads={uploads}
                                    selected={selected}
                                    onSelect={setSelected}
                                    onRemove={removeUpload} />
                ) : null}

                {worksheet ? (
                    <Worksheet ws={worksheet} tab={tab} setTab={setTab}
                                onPostLegA={postLegA} onReverseLegA={reverseLegA}
                                openPayouts={openPayouts}
                                onBridge={bridgePayout}
                                onUnbridge={unbridgePayout}
                                mappingQueue={mappingQueue}
                                customerLedgers={customerLedgers}
                                onMapCustomer={mapCustomer}
                                onReverseLegB={reverseLegB} />
                ) : uploads.length === 0 && !uploading ? (
                    <div className="text-sm text-stone-500 text-center py-16 border rounded-md bg-white"
                         style={{ borderColor: "var(--se-border)" }}>
                        No uploads yet. Pick a provider above and drop the report files.
                    </div>
                ) : null}

                {/* Stage 4 — GST Invoice Ingest + ITC Release */}
                <GstInvoicePanel invoices={gstInvoices}
                                    onUpload={uploadGstInvoice}
                                    onReleaseItc={releaseItc}
                                    onReverseItc={reverseItc}
                                    onDeleteInvoice={deleteInvoice} />

                {/* Stage 4 — Adjustments / Recovery */}
                {openAdjustments.length ? (
                    <AdjustmentsPanel adjustments={openAdjustments}
                                        onPost={postAdjustment}
                                        onReverse={reverseAdjustment} />
                ) : null}
            </div>
        </>
    );
}


function UploadCard({ onUploaded, uploading, setUploading }) {
    const [provider, setProvider] = useState("razorpay");
    const fileRef = useRef(null);
    const [staged, setStaged] = useState([]);

    const submit = async () => {
        if (!staged.length) return toast.error("Attach the provider report file(s).");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("provider", provider);
            staged.forEach((f) => fd.append("files", f));
            const { data } = await api.post("/pg-recon/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const summary = data.files.map((f) => `${f.kind} · ${f.row_count} rows`).join("; ");
            toast.success(`Parsed: ${summary}`);
            setStaged([]);
            if (fileRef.current) fileRef.current.value = "";
            onUploaded(data.id);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };

    return (
        <div className="bg-white border rounded-md p-5"
             style={{ borderColor: "var(--se-border)" }}
             data-testid="pg-recon-upload-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="font-serif text-lg">Upload provider report</div>
                    <div className="text-xs text-stone-500 mt-1">
                        Razorpay: Payments + Settlements together. Pine Labs: All-Transactions CSV + MPR xlsx together.
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <select value={provider} onChange={(e) => setProvider(e.target.value)}
                            className="border rounded-sm px-3 py-2 text-sm"
                            data-testid="pg-recon-provider-select"
                            disabled={uploading}>
                        <option value="razorpay">Razorpay</option>
                        <option value="pine_labs">Pine Labs</option>
                    </select>
                    <input type="file"
                            ref={fileRef}
                            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                            multiple
                            onChange={(e) => setStaged(Array.from(e.target.files || []))}
                            className="text-xs"
                            disabled={uploading}
                            data-testid="pg-recon-file-input" />
                    <Button onClick={submit}
                            disabled={uploading || !staged.length}
                            style={{ background: "#7c3aed", color: "#fff" }}
                            data-testid="pg-recon-upload-btn">
                        <FileArrowUp size={16} className="mr-2" />
                        {uploading ? "Parsing…" : `Parse ${staged.length || ""} file${staged.length === 1 ? "" : "s"}`}
                    </Button>
                </div>
            </div>
            {staged.length ? (
                <div className="mt-3 text-[11px] text-stone-500 font-mono">
                    Attached: {staged.map((f) => f.name).join(" · ")}
                </div>
            ) : null}
        </div>
    );
}


function UploadPicker({ uploads, selected, onSelect, onRemove }) {
    return (
        <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] uppercase tracking-widest text-stone-500">Worksheet</span>
            <select value={selected || ""} onChange={(e) => onSelect(e.target.value)}
                    className="border rounded-sm px-3 py-2 text-sm min-w-[400px]"
                    data-testid="pg-recon-worksheet-picker">
                {uploads.map((u) => (
                    <option key={u.id} value={u.id}>
                        {u.provider === "razorpay" ? "Razorpay" : "Pine Labs"} · {u.uploaded_at.slice(0, 10)} · {u.files.map((f) => `${f.kind.replace(/^razorpay_|^pine_/, "")} ${f.row_count}`).join(" + ")}
                    </option>
                ))}
            </select>
            {selected ? (
                <Button size="sm" variant="outline"
                        onClick={() => onRemove(selected)}
                        style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                        data-testid="pg-recon-discard-btn">
                    <TrashSimple size={14} className="mr-1" /> Discard
                </Button>
            ) : null}
        </div>
    );
}


function Worksheet({ ws, tab, setTab, onPostLegA, onReverseLegA, openPayouts, onBridge, onUnbridge, mappingQueue, customerLedgers, onMapCustomer, onReverseLegB }) {
    const { upload, sections } = ws;
    const t = sections.totals || {};
    const posted = t.leg_a_posted_count || 0;
    const pending = t.leg_a_pending_count || 0;
    const bridgedCount = t.bridged_count || 0;
    const bridgePending = t.bridge_pending_count || 0;
    const legBPosted = t.leg_b_posted_count || 0;
    const legBPending = t.leg_b_pending_count || 0;
    // Filter payouts to the current upload
    const uploadPayouts = (openPayouts || []).filter((p) => p.upload_id === upload.id);
    const failedCrossChecks = useMemo(() => {
        const buckets = [];
        Object.values(sections).forEach((v) => {
            if (Array.isArray(v)) v.forEach((row) => {
                (row.cross_checks || []).forEach((c) => { if (c.ok === false) buckets.push({ ...c, row }); });
                if (row.mpr) (row.mpr.cross_checks || []).forEach((c) => { if (c.ok === false) buckets.push({ ...c, row: row.mpr }); });
            });
        });
        return buckets;
    }, [sections]);

    return (
        <div className="space-y-6">
            {/* Leg A posting bar */}
            <div className="bg-white border rounded-md px-4 py-3 flex items-center gap-4 flex-wrap"
                 style={{ borderColor: "var(--se-border)" }}
                 data-testid="pg-recon-post-bar">
                <div className="flex-1 text-sm">
                    <div className="font-medium">Stage 2 — Leg A postings</div>
                    <div className="text-[11px] text-stone-500">
                        Posted {posted} · Pending {pending} · Every voucher: Dr Gateway Charges + Dr Pending-GST + Dr In-Transit → Cr Suspense
                    </div>
                </div>
                <Button onClick={onPostLegA}
                        disabled={!pending}
                        style={{ background: pending ? "#7c3aed" : "#e5e7eb",
                                 color: pending ? "#fff" : "#9ca3af" }}
                        data-testid="pg-recon-post-leg-a">
                    Post Leg A ({pending})
                </Button>
                <Button variant="outline" onClick={onReverseLegA}
                        disabled={!posted}
                        style={{ color: posted ? "#b91c1c" : undefined,
                                 borderColor: posted ? "#fecaca" : undefined }}
                        data-testid="pg-recon-reverse-leg-a">
                    Reverse ({posted})
                </Button>
            </div>

            {/* Stage 2b — UTR ↔ Bank Bridge */}
            {uploadPayouts.length ? (
                <PayoutBridgePanel payouts={uploadPayouts}
                                    bridgedCount={bridgedCount}
                                    bridgePending={bridgePending}
                                    onBridge={onBridge}
                                    onUnbridge={onUnbridge} />
            ) : null}

            {/* Stage 3 — Customer Mapping & Leg B */}
            <CustomerMappingPanel queue={mappingQueue}
                                    legBPosted={legBPosted}
                                    legBPending={legBPending}
                                    customerLedgers={customerLedgers}
                                    onMap={onMapCustomer}
                                    onReverseLegB={onReverseLegB} />

            {/* Warnings */}
            {upload.warnings?.length ? (
                <div className="border rounded-sm p-3 text-sm"
                     style={{ background: "#fef3c7", borderColor: "#fbbf24", color: "#78350f" }}>
                    <div className="font-medium flex items-center gap-2"><Warning size={14} weight="fill" /> Parser warnings</div>
                    <ul className="mt-1 text-[11px] list-disc pl-5">
                        {upload.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                </div>
            ) : null}
            {failedCrossChecks.length ? (
                <div className="border rounded-sm p-3 text-sm"
                     style={{ background: "#fee2e2", borderColor: "#fca5a5", color: "#7f1d1d" }}
                     data-testid="pg-recon-crosscheck-fails">
                    <div className="font-medium flex items-center gap-2"><XCircle size={14} weight="fill" /> {failedCrossChecks.length} cross-check failure{failedCrossChecks.length === 1 ? "" : "s"}</div>
                    <ul className="mt-1 text-[11px] list-disc pl-5 max-h-40 overflow-auto">
                        {failedCrossChecks.slice(0, 20).map((c, i) => (<li key={i}><b>{c.id}</b>: {c.detail}</li>))}
                        {failedCrossChecks.length > 20 ? <li className="italic">…and {failedCrossChecks.length - 20} more</li> : null}
                    </ul>
                </div>
            ) : null}

            {/* Totals tiles */}
            <TotalsTiles provider={upload.provider} t={t} />

            {/* Tabs */}
            {upload.provider === "razorpay" ? (
                <>
                    <TabBar tab={tab} setTab={setTab} tabs={[
                        { id: "payments", label: `Payments (${sections.payments?.length || 0})` },
                        { id: "settlements", label: `Settlements (${sections.settlements?.length || 0})` },
                    ]} />
                    {tab === "payments" ? <RzpPaymentsTable rows={sections.payments || []} /> : null}
                    {tab === "settlements" ? <RzpSettlementsTable rows={sections.settlements || []} /> : null}
                </>
            ) : (
                <>
                    <TabBar tab={tab} setTab={setTab} tabs={[
                        { id: "transactions", label: `Transactions (${sections.joined_transactions?.length || 0})` },
                        { id: "payouts", label: `Payouts (${sections.payouts?.length || 0})` },
                        { id: "recovery", label: `Recovery / Adjustments (${sections.recovery_adjustments?.length || 0})` },
                    ]} />
                    {tab === "transactions" ? <PineTxnsTable rows={sections.joined_transactions || []} /> : null}
                    {tab === "payouts" ? <PinePayoutsTable rows={sections.payouts || []} /> : null}
                    {tab === "recovery" ? <PineRecoveryTable rows={sections.recovery_adjustments || []} /> : null}
                </>
            )}
        </div>
    );
}


function TotalsTiles({ provider, t }) {
    const tiles = provider === "razorpay" ? [
        { l: "Captured payments", v: t.captured_count, m: `${t.settlements_count || 0} settlements` },
        { l: "Gross collected", v: `₹ ${inr(t.gross_captured || 0)}`, m: `Fee: ₹ ${inr(t.fee_captured || 0)}, GST: ₹ ${inr(t.gst_captured || 0)}` },
        { l: "Net captured", v: `₹ ${inr(t.net_captured || 0)}`, m: `after fee + GST` },
        { l: "In-transit / on-hold", v: `₹ ${inr(t.in_transit_or_hold || 0)}`, m: "net captured − net settled", danger: (t.in_transit_or_hold || 0) > (t.net_captured || 0) * 0.5 },
    ] : [
        { l: "Customer txns (settled)", v: t.customer_txn_count, m: `${t.settled_txn_count} joined w/ MPR, ${t.pending_settlement_count} pending` },
        { l: "Gross to Pine Labs", v: `₹ ${inr(t.gross_settled || 0)}`, m: `Fee: ₹ ${inr(t.fee_total || 0)}, GST: ₹ ${inr(t.gst_total || 0)}` },
        { l: "Net paid to merchant", v: `₹ ${inr(t.net_paid_to_merchant || 0)}`, m: "sum of MPR 'Paid to Merchant A/c'" },
        { l: "Payouts (deduped)", v: `₹ ${inr(t.payouts_total || 0)}`, m: `${t.recovery_count || 0} adjustments`, danger: t.recovery_count > 0 },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tiles.map((tile, i) => (
                <div key={i} className="bg-white border rounded-md p-4"
                     style={{ borderColor: tile.danger ? "#fecaca" : "var(--se-border)",
                              background: tile.danger ? "#fef2f2" : "#fff" }}
                     data-testid={`pg-recon-tile-${i}`}>
                    <div className="text-[11px] uppercase tracking-widest text-stone-500">{tile.l}</div>
                    <div className="text-2xl font-serif mt-1"
                         style={{ color: tile.danger ? "#b91c1c" : "#292524" }}>{tile.v}</div>
                    <div className="text-[10px] text-stone-500 mt-1">{tile.m}</div>
                </div>
            ))}
        </div>
    );
}


function TabBar({ tab, setTab, tabs }) {
    return (
        <div className="border-b flex gap-1" style={{ borderColor: "var(--se-border)" }}>
            {tabs.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)}
                        className="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
                        style={{ borderColor: tab === t.id ? "#7c3aed" : "transparent",
                                 color: tab === t.id ? "#7c3aed" : "#57534e" }}
                        data-testid={`pg-recon-tab-${t.id}`}>
                    {t.label}
                </button>
            ))}
        </div>
    );
}


function CheckPill({ checks }) {
    const bad = (checks || []).filter((c) => c.ok === false);
    if (!bad.length) return <CheckCircle size={12} weight="fill" style={{ color: "#059669" }} />;
    return (
        <span title={bad.map((b) => b.detail).join("\n")}>
            <XCircle size={12} weight="fill" style={{ color: "#dc2626" }} />
        </span>
    );
}


function RzpPaymentsTable({ rows }) {
    return (
        <div className="border rounded-md overflow-auto bg-white" style={{ borderColor: "var(--se-border)" }}>
            <table className="se-table text-sm w-full" data-testid="pg-recon-rzp-payments-table">
                <thead><tr>
                    <th>✓</th><th>payment_id</th><th>date</th><th>method</th><th>contact</th>
                    <th className="text-right">gross</th>
                    <th className="text-right">MDR</th>
                    <th className="text-right">GST</th>
                    <th className="text-right">fee (incl GST)</th>
                    <th className="text-right">net</th>
                    <th>RRN / ARN</th>
                </tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.payment_id}>
                            <td><CheckPill checks={r.cross_checks} /></td>
                            <td className="font-mono text-[11px]">{r.payment_id}</td>
                            <td className="text-[11px]">{r.created_at}</td>
                            <td className="text-[11px]">{r.method} {r.card_network ? `· ${r.card_network}` : ""}</td>
                            <td className="font-mono text-[11px]">{r.contact}</td>
                            <td className="se-num text-right">{inr(r.gross_amount || 0)}</td>
                            <td className="se-num text-right">{inr(r.mdr_pre_gst || 0)}</td>
                            <td className="se-num text-right">{inr(r.gst_on_fee || 0)}</td>
                            <td className="se-num text-right">{inr(r.fee_gst_inclusive || 0)}</td>
                            <td className="se-num text-right"><b>{inr(r.net_to_merchant || 0)}</b></td>
                            <td className="font-mono text-[10px]">{r.rrn || "—"} / {r.arn || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function RzpSettlementsTable({ rows }) {
    return (
        <div className="border rounded-md overflow-auto bg-white" style={{ borderColor: "var(--se-border)" }}>
            <table className="se-table text-sm w-full" data-testid="pg-recon-rzp-settlements-table">
                <thead><tr>
                    <th>settlement_id</th><th>date</th><th className="text-right">net settled</th>
                    <th>UTR (primary)</th><th>UTR (additional)</th><th>status</th>
                </tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.settlement_id}>
                            <td className="font-mono text-[11px]">{r.settlement_id}</td>
                            <td className="text-[11px]">{r.created_at}</td>
                            <td className="se-num text-right"><b>{inr(r.net_settled || 0)}</b></td>
                            <td className="font-mono text-[10px]">{r.utr_primary || "—"}</td>
                            <td className="font-mono text-[10px]">{r.utr_additional || "—"}</td>
                            <td className="text-[11px]">{r.status}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function PineTxnsTable({ rows }) {
    return (
        <div className="border rounded-md overflow-auto bg-white" style={{ borderColor: "var(--se-border)" }}>
            <table className="se-table text-sm w-full" data-testid="pg-recon-pine-txns-table">
                <thead><tr>
                    <th>✓</th><th>customer</th><th>txn date</th><th>RRN / Auth</th>
                    <th className="text-right">gross</th>
                    <th className="text-right">MDR</th>
                    <th className="text-right">Platform</th>
                    <th className="text-right">GST</th>
                    <th className="text-right">total fee</th>
                    <th className="text-right">net</th>
                    <th>payout UTR</th>
                </tr></thead>
                <tbody>
                    {rows.map((r, i) => {
                        const m = r.mpr;
                        return (
                            <tr key={i} style={{ background: r.pending_settlement ? "#fef3c7" : "transparent" }}>
                                <td><CheckPill checks={[...(r.cross_checks || []), ...((m?.cross_checks) || [])]} /></td>
                                <td className="text-[11px]">{r.customer_name}</td>
                                <td className="text-[11px]">{r.txn_date}</td>
                                <td className="font-mono text-[10px]">{r.rrn_norm} / {r.auth_norm}</td>
                                <td className="se-num text-right">{inr(r.gross_amount || 0)}</td>
                                <td className="se-num text-right">{m ? inr(m.mdr_pre_gst || 0) : <span className="text-amber-700 italic">pending</span>}</td>
                                <td className="se-num text-right">{m ? inr(m.platform_fee || 0) : "—"}</td>
                                <td className="se-num text-right">{m ? inr(m.gst_on_fee || 0) : "—"}</td>
                                <td className="se-num text-right">{m ? inr(m.total_fee_incl_gst || 0) : "—"}</td>
                                <td className="se-num text-right">{m ? <b>{inr(m.net_to_merchant || 0)}</b> : "—"}</td>
                                <td className="font-mono text-[10px]">{m?.payout_utr || "—"}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


function PinePayoutsTable({ rows }) {
    return (
        <div className="border rounded-md overflow-auto bg-white" style={{ borderColor: "var(--se-border)" }}>
            <table className="se-table text-sm w-full" data-testid="pg-recon-pine-payouts-table">
                <thead><tr>
                    <th>payout UTR</th><th>payout date</th>
                    <th className="text-right">total</th>
                    <th className="text-right">AR recovery</th>
                    <th className="text-right">other adj</th>
                    <th className="text-right">net payable</th>
                    <th className="text-right">final payout</th>
                    <th>UTR2</th>
                </tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.payout_utr}>
                            <td className="font-mono text-[10px]">{r.payout_utr}</td>
                            <td className="text-[11px]">{r.payout_date || "—"}</td>
                            <td className="se-num text-right">{inr(r.total || 0)}</td>
                            <td className="se-num text-right"
                                style={{ color: (r.ar_recovery || 0) > 0 ? "#b91c1c" : undefined }}>
                                {inr(r.ar_recovery || 0)}
                            </td>
                            <td className="se-num text-right">{inr((r.refund || 0) + (r.chargeback_recovery || 0) + (r.loan_recovery || 0) + (r.other_recoveries || 0))}</td>
                            <td className="se-num text-right">{inr(r.net_payable || 0)}</td>
                            <td className="se-num text-right"><b>{inr(r.final_payout || 0)}</b></td>
                            <td className="font-mono text-[10px]">{r.utr2 || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function PineRecoveryTable({ rows }) {
    if (!rows.length) return <div className="text-sm text-stone-500 text-center py-8">No recovery/adjustment rows this cycle.</div>;
    return (
        <div className="border rounded-md overflow-auto bg-white" style={{ borderColor: "var(--se-border)" }}>
            <table className="se-table text-sm w-full" data-testid="pg-recon-pine-recovery-table">
                <thead><tr>
                    <th>Type</th><th>Ref ID</th><th>date</th><th>CR/DR</th>
                    <th className="text-right">total</th>
                    <th className="text-right">adjusted</th>
                    <th className="text-right">outstanding</th>
                    <th>RRN</th><th>UTR</th>
                </tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td className="text-[11px]">{r.adjustment_type}</td>
                            <td className="font-mono text-[10px]">{r.reference_id}</td>
                            <td className="text-[11px]">{r.adjustment_date}</td>
                            <td className="text-[11px]">{r.cr_dr}</td>
                            <td className="se-num text-right">{inr(r.total_amount || 0)}</td>
                            <td className="se-num text-right">{inr(r.adjusted_amount || 0)}</td>
                            <td className="se-num text-right">{inr(r.outstanding_amount || 0)}</td>
                            <td className="font-mono text-[10px]">{r.rrn_norm}</td>
                            <td className="font-mono text-[10px]">{r.utr || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}



function PayoutBridgePanel({ payouts, bridgedCount, bridgePending, onBridge, onUnbridge }) {
    const pending = payouts.filter((p) => !p.bridged);
    const bridged = payouts.filter((p) => p.bridged);
    return (
        <div className="bg-white border rounded-md"
             style={{ borderColor: "var(--se-border)" }}
             data-testid="pg-recon-bridge-panel">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-4 flex-wrap"
                 style={{ borderColor: "var(--se-border)" }}>
                <div className="text-sm">
                    <div className="font-medium">Stage 2b — UTR ↔ Bank bridge</div>
                    <div className="text-[11px] text-stone-500">
                        Bridged {bridgedCount} · Pending {bridgePending} · Bridge posts Dr Bank / Cr Gateway In-Transit at the exact bank credit amount (₹1 drift auto-absorbed).
                    </div>
                </div>
            </div>
            <div className="overflow-auto">
                <table className="se-table text-sm w-full" data-testid="pg-recon-bridge-table">
                    <thead><tr>
                        <th>Provider</th>
                        <th>UTR</th>
                        <th>Date</th>
                        <th className="text-right">Amount</th>
                        <th>Status</th>
                        <th>Suggested bank line</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {pending.map((p) => (
                            <tr key={p.payout_key}>
                                <td className="text-[11px]">{p.provider === "razorpay" ? "Razorpay" : "Pine Labs"}</td>
                                <td className="font-mono text-[10px]">{p.utr || "—"}</td>
                                <td className="text-[11px]">{p.date || "—"}</td>
                                <td className="se-num text-right"><b>{inr(p.amount || 0)}</b></td>
                                <td>
                                    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300">
                                        awaiting bank match
                                    </span>
                                </td>
                                <td>
                                    {p.suggested_bank_line ? (
                                        <div className="text-[11px]">
                                            <div className="font-mono text-[10px]">{p.suggested_bank_line.bank} · {p.suggested_bank_line.date}</div>
                                            <div>₹{inr(p.suggested_bank_line.credit || 0)} · {p.suggested_bank_line.ref || "no ref"}</div>
                                        </div>
                                    ) : (
                                        <span className="text-[11px] text-stone-400 italic">no auto-match</span>
                                    )}
                                </td>
                                <td>
                                    {p.suggested_bank_line ? (
                                        <Button size="sm"
                                                onClick={() => onBridge(p.payout_key, p.suggested_bank_line.bank_line_id)}
                                                style={{ background: "#7c3aed", color: "#fff" }}
                                                data-testid={`pg-recon-bridge-btn-${p.payout_key}`}>
                                            Bridge →
                                        </Button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                        {bridged.map((p) => (
                            <tr key={p.payout_key} style={{ background: "#f0fdf4" }}>
                                <td className="text-[11px]">{p.provider === "razorpay" ? "Razorpay" : "Pine Labs"}</td>
                                <td className="font-mono text-[10px]">{p.utr || "—"}</td>
                                <td className="text-[11px]">{p.date || "—"}</td>
                                <td className="se-num text-right"><b>{inr(p.amount || 0)}</b></td>
                                <td>
                                    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300">
                                        <CheckCircle size={10} weight="fill" className="inline mr-1" />
                                        bridged
                                    </span>
                                </td>
                                <td className="font-mono text-[10px] text-stone-500">
                                    journal {p.bank_bridge_journal_id?.slice(0, 8)}…
                                </td>
                                <td>
                                    <Button size="sm" variant="outline"
                                            onClick={() => onUnbridge(p.payout_key)}
                                            style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                                            data-testid={`pg-recon-unbridge-btn-${p.payout_key}`}>
                                        Unbridge
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function CustomerMappingPanel({ queue, legBPosted, legBPending, customerLedgers, onMap, onReverseLegB }) {
    const [openBucket, setOpenBucket] = useState(null);
    const pending = (queue || []).filter((b) => !b.mapped_customer_ledger_id);
    return (
        <div className="bg-white border rounded-md"
             style={{ borderColor: "var(--se-border)" }}
             data-testid="pg-recon-mapping-panel">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-4 flex-wrap"
                 style={{ borderColor: "var(--se-border)" }}>
                <div className="text-sm">
                    <div className="font-medium">Stage 3 — Customer mapping & Leg B</div>
                    <div className="text-[11px] text-stone-500">
                        Leg B posted {legBPosted} · Pending {legBPending} · Map is learn-once (phone / name → customer ledger) then Leg B auto-posts Dr Suspense / Cr Customer.
                    </div>
                </div>
                {legBPosted ? (
                    <Button size="sm" variant="outline"
                            onClick={onReverseLegB}
                            style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                            data-testid="pg-recon-reverse-leg-b">
                        Reverse Leg B ({legBPosted})
                    </Button>
                ) : null}
            </div>
            {!pending.length ? (
                <div className="text-[11px] text-stone-500 text-center py-8">
                    {legBPosted ? "All Leg-A-posted transactions on this upload are attributed to customers." : "No mapping queue yet — post Leg A first."}
                </div>
            ) : (
                <div className="overflow-auto">
                    <table className="se-table text-sm w-full" data-testid="pg-recon-mapping-table">
                        <thead><tr>
                            <th>Provider</th>
                            <th>Match key</th>
                            <th>Value</th>
                            <th className="text-right">Txn count</th>
                            <th className="text-right">Gross total</th>
                            <th></th>
                        </tr></thead>
                        <tbody>
                            {pending.map((b) => (
                                <tr key={`${b.provider}-${b.key_type}-${b.key_norm}`}
                                    style={{ background: openBucket === b.key_norm ? "#fef3c7" : "transparent" }}>
                                    <td className="text-[11px]">{b.provider === "razorpay" ? "Razorpay" : "Pine Labs"}</td>
                                    <td className="text-[11px]">{b.key_type}</td>
                                    <td className="font-mono text-[11px]">{b.display}</td>
                                    <td className="se-num text-right">{b.txn_count}</td>
                                    <td className="se-num text-right"><b>{inr(b.gross_total || 0)}</b></td>
                                    <td>
                                        <Button size="sm"
                                                onClick={() => setOpenBucket(openBucket === b.key_norm ? null : b.key_norm)}
                                                style={{ background: "#7c3aed", color: "#fff" }}
                                                data-testid={`pg-recon-map-btn-${b.key_norm}`}>
                                            Map →
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {openBucket ? (
                <MapCustomerDialog bucket={pending.find((b) => b.key_norm === openBucket)}
                                    customerLedgers={customerLedgers}
                                    onCancel={() => setOpenBucket(null)}
                                    onConfirm={(choice) => { onMap(pending.find((b) => b.key_norm === openBucket), choice); setOpenBucket(null); }} />
            ) : null}
        </div>
    );
}


function MapCustomerDialog({ bucket, customerLedgers, onCancel, onConfirm }) {
    const [mode, setMode] = useState("existing");
    const [ledgerId, setLedgerId] = useState("");
    const [advName, setAdvName] = useState(bucket.key_type === "name" ? bucket.display : "");
    const [gstin, setGstin] = useState("");
    if (!bucket) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
             style={{ background: "rgba(0,0,0,0.35)" }}
             data-testid="pg-recon-map-dialog">
            <div className="bg-white rounded-md border shadow-lg w-full max-w-xl"
                 style={{ borderColor: "var(--se-border)" }}>
                <div className="px-5 py-4 border-b" style={{ borderColor: "var(--se-border)" }}>
                    <div className="text-lg font-serif">Map to customer</div>
                    <div className="text-[11px] text-stone-500 mt-1">
                        {bucket.provider === "razorpay" ? "Razorpay" : "Pine Labs"} · {bucket.key_type} <b>{bucket.display}</b> · {bucket.txn_count} txn · ₹{inr(bucket.gross_total)}
                    </div>
                </div>
                <div className="p-5 space-y-4">
                    <div className="flex gap-4 text-xs">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" checked={mode === "existing"} onChange={() => setMode("existing")}
                                    data-testid="pg-recon-map-mode-existing" />
                            Pick existing customer ledger
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" checked={mode === "new"} onChange={() => setMode("new")}
                                    data-testid="pg-recon-map-mode-new" />
                            Create new customer
                        </label>
                    </div>
                    {mode === "existing" ? (
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-stone-500">Customer ledger</label>
                            <select value={ledgerId} onChange={(e) => setLedgerId(e.target.value)}
                                    className="border rounded-sm px-3 py-2 text-sm w-full mt-1"
                                    data-testid="pg-recon-map-ledger">
                                <option value="">— pick a customer —</option>
                                {(customerLedgers || []).map((l) => (
                                    <option key={l.id} value={l.id}>{l.name} {l.gstin ? `· ${l.gstin}` : ""}</option>
                                ))}
                            </select>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-stone-500">Customer name (advance name)</label>
                                <input type="text" value={advName} onChange={(e) => setAdvName(e.target.value)}
                                        className="border rounded-sm px-3 py-2 text-sm w-full mt-1"
                                        placeholder="e.g. Ambarish B"
                                        data-testid="pg-recon-map-new-name" />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-stone-500">GSTIN (optional, B2B only)</label>
                                <input type="text" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                                        className="border rounded-sm px-3 py-2 text-sm w-full mt-1"
                                        placeholder="29XXXXXXXXXXX1Z5"
                                        data-testid="pg-recon-map-new-gstin" />
                            </div>
                            {bucket.key_type === "phone" ? (
                                <div className="text-[11px] text-stone-500">
                                    Phone <b>{bucket.display}</b> will be attached as `advance_phone` on the new ledger.
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t flex justify-end gap-2"
                     style={{ borderColor: "var(--se-border)" }}>
                    <Button variant="outline" onClick={onCancel}
                            data-testid="pg-recon-map-cancel">Cancel</Button>
                    <Button
                        onClick={() => {
                            if (mode === "existing") {
                                if (!ledgerId) return toast.error("Pick a customer ledger.");
                                onConfirm({ customer_ledger_id: ledgerId });
                            } else {
                                if (!advName.trim()) return toast.error("Enter a customer name.");
                                onConfirm({
                                    new_customer_parts: {
                                        advance_name: advName.trim(),
                                        ...(bucket.key_type === "phone" ? { advance_phone: bucket.key_norm } : {}),
                                    },
                                    gstin: gstin.trim() || null,
                                });
                            }
                        }}
                        style={{ background: "#7c3aed", color: "#fff" }}
                        data-testid="pg-recon-map-confirm">
                        Map & post Leg B ({bucket.txn_count})
                    </Button>
                </div>
            </div>
        </div>
    );
}



function GstInvoicePanel({ invoices, onUpload, onReleaseItc, onReverseItc, onDeleteInvoice }) {
    const [provider, setProvider] = useState("razorpay");
    const [month, setMonth] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().slice(0, 7);
    });
    const [staged, setStaged] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!staged) return toast.error("Attach the provider's GST invoice PDF.");
        setBusy(true);
        try { await onUpload(provider, month, staged); setStaged(null); }
        finally { setBusy(false); }
    };
    return (
        <div className="bg-white border rounded-md"
             style={{ borderColor: "var(--se-border)" }}
             data-testid="pg-recon-invoice-panel">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-4 flex-wrap"
                 style={{ borderColor: "var(--se-border)" }}>
                <div className="text-sm">
                    <div className="font-medium">Stage 4 — Monthly GST invoice · ITC release</div>
                    <div className="text-[11px] text-stone-500">
                        Upload the provider&apos;s tax invoice PDF. AI extracts totals, runs a 3-way match against parsed reports, then releases ITC from Pending → Input CGST/SGST/IGST.
                    </div>
                </div>
            </div>
            <div className="px-4 py-3 border-b flex items-center gap-3 flex-wrap"
                 style={{ borderColor: "var(--se-border)" }}>
                <select value={provider} onChange={(e) => setProvider(e.target.value)}
                        className="border rounded-sm px-3 py-2 text-sm"
                        data-testid="pg-invoice-provider">
                    <option value="razorpay">Razorpay</option>
                    <option value="pine_labs">Pine Labs</option>
                </select>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                        className="border rounded-sm px-3 py-2 text-sm"
                        data-testid="pg-invoice-month" />
                <input type="file" accept=".pdf,application/pdf"
                        onChange={(e) => setStaged(e.target.files?.[0] || null)}
                        className="text-xs"
                        data-testid="pg-invoice-file" />
                <Button onClick={submit} disabled={busy || !staged}
                        style={{ background: "#7c3aed", color: "#fff" }}
                        data-testid="pg-invoice-upload-btn">
                    <FileArrowUp size={14} className="mr-2" />
                    {busy ? "Extracting…" : "Upload & extract"}
                </Button>
            </div>
            <div className="overflow-auto">
                <table className="se-table text-sm w-full" data-testid="pg-recon-invoice-table">
                    <thead><tr>
                        <th>Provider</th>
                        <th>Month</th>
                        <th>Invoice #</th>
                        <th>Date</th>
                        <th className="text-right">Taxable</th>
                        <th className="text-right">CGST/SGST/IGST</th>
                        <th className="text-right">Total GST</th>
                        <th>3-way match</th>
                        <th>ITC status</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {!invoices.length ? (
                            <tr><td colSpan={10} className="text-center py-6 text-stone-500 text-[11px]">
                                No GST invoices uploaded yet.
                            </td></tr>
                        ) : invoices.map((inv) => (
                            <tr key={inv.id}>
                                <td className="text-[11px]">{inv.provider === "razorpay" ? "Razorpay" : "Pine Labs"}</td>
                                <td className="text-[11px]">{inv.month}</td>
                                <td className="font-mono text-[10px]">{inv.invoice_no || "—"}</td>
                                <td className="text-[11px]">{inv.invoice_date || "—"}</td>
                                <td className="se-num text-right">{inr(inv.taxable || 0)}</td>
                                <td className="se-num text-right text-[10px]">
                                    {inr(inv.cgst || 0)} / {inr(inv.sgst || 0)} / {inr(inv.igst || 0)}
                                </td>
                                <td className="se-num text-right"><b>{inr(inv.gst_total || 0)}</b></td>
                                <td>
                                    {inv.three_way_match?.matched ? (
                                        <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300">
                                            <CheckCircle size={10} weight="fill" className="inline mr-1" />
                                            match
                                        </span>
                                    ) : (
                                        <span className="text-[10px] px-2 py-0.5 rounded-sm bg-red-100 text-red-800 border border-red-300"
                                              title={`drift ₹${inv.three_way_match?.drift}`}>
                                            <XCircle size={10} weight="fill" className="inline mr-1" />
                                            drift ₹{inv.three_way_match?.drift}
                                        </span>
                                    )}
                                    <div className="text-[10px] text-stone-500 mt-0.5">
                                        vs reports ₹{inv.three_way_match?.expected_gst_from_reports}
                                    </div>
                                </td>
                                <td>
                                    {inv.itc_release_journal_id ? (
                                        <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300">
                                            released
                                        </span>
                                    ) : (
                                        <span className="text-[10px] px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300">
                                            pending
                                        </span>
                                    )}
                                </td>
                                <td className="text-right">
                                    {inv.itc_release_journal_id ? (
                                        <Button size="sm" variant="outline"
                                                onClick={() => onReverseItc(inv.id)}
                                                style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                                                data-testid={`pg-invoice-reverse-${inv.id}`}>
                                            Reverse ITC
                                        </Button>
                                    ) : (
                                        <div className="flex gap-1 justify-end">
                                            <Button size="sm" variant="outline"
                                                    onClick={() => onDeleteInvoice(inv.id)}
                                                    data-testid={`pg-invoice-delete-${inv.id}`}>
                                                <TrashSimple size={12} />
                                            </Button>
                                            <Button size="sm"
                                                    onClick={() => onReleaseItc(inv.id, null)}
                                                    style={{ background: "#7c3aed", color: "#fff" }}
                                                    data-testid={`pg-invoice-release-${inv.id}`}>
                                                Release ITC
                                            </Button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function AdjustmentsPanel({ adjustments, onPost, onReverse }) {
    return (
        <div className="bg-white border rounded-md"
             style={{ borderColor: "var(--se-border)" }}
             data-testid="pg-recon-adjustments-panel">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-4 flex-wrap"
                 style={{ borderColor: "var(--se-border)" }}>
                <div className="text-sm">
                    <div className="font-medium">Stage 4 — Adjustments / Recovery</div>
                    <div className="text-[11px] text-stone-500">
                        Provider AR-recovery, chargeback recovery, refund adjustments. Posts to a dedicated review bucket — customer ledgers are NOT auto-reversed.
                    </div>
                </div>
            </div>
            <div className="overflow-auto">
                <table className="se-table text-sm w-full" data-testid="pg-recon-adjustments-table">
                    <thead><tr>
                        <th>Type</th>
                        <th>Reference</th>
                        <th>Date</th>
                        <th className="text-right">Outstanding</th>
                        <th>Status</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {adjustments.map((a) => (
                            <tr key={a.natural_key}>
                                <td className="text-[11px]">{a.adjustment_type}</td>
                                <td className="font-mono text-[10px]">{a.reference_id || a.natural_key}</td>
                                <td className="text-[11px]">{a.adjustment_date || "—"}</td>
                                <td className="se-num text-right"><b>{inr(a.outstanding_amount || a.total_amount || 0)}</b></td>
                                <td>
                                    {a.adjustment_journal_id ? (
                                        <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300">
                                            posted
                                        </span>
                                    ) : (
                                        <span className="text-[10px] px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300">
                                            open
                                        </span>
                                    )}
                                </td>
                                <td className="text-right">
                                    {a.adjustment_journal_id ? (
                                        <Button size="sm" variant="outline"
                                                onClick={() => onReverse(a.natural_key)}
                                                style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                                                data-testid={`pg-adj-reverse-${a.natural_key}`}>
                                            Reverse
                                        </Button>
                                    ) : (
                                        <Button size="sm"
                                                onClick={() => onPost(a.natural_key)}
                                                style={{ background: "#7c3aed", color: "#fff" }}
                                                data-testid={`pg-adj-post-${a.natural_key}`}>
                                            Post to Recovery
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

